import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { MswipeService, MSwipePaymentResponse } from './mswipe.service';

export interface RechargePlan {
  id: number;
  amount: number;
  coins: number;
  bonus_coins: number;
  total_coins: number;
  description: string;
}

export interface PaymentResponse {
  status: string;
  message: string;
  payment_data: any;
  transaction_id: string;
  mswipe_response?: MSwipePaymentResponse;
}

export interface VerifyPaymentRequest {
  transaction_id: string;
  amount?: number; 
  mswipe_trans_id?: string;
  trans_id?: string;
}

export interface VerifyPaymentResponse {
  status: string;
  message: string;
  coins_added: number;
  amount_added: number;
  new_balance: number;
  new_coins: number;
}

@Injectable({
  providedIn: 'root'
})
export class RewardsService {
  private apiUrl = 'https://stories.funtelstudio.com/storyapi/rewards_api.php';
  private plansUrl = 'https://stories.funtelstudio.com/storyapi/get_recharge_plans.php';
  private paymentUrl = 'https://stories.funtelstudio.com/storyapi/initiate_payment.php';
  private verifyUrl = 'https://stories.funtelstudio.com/storyapi/verify_payment.php';

  constructor(
    private http: HttpClient,
    private mswipeService: MswipeService
  ) { }

 getRechargePlans(): Observable<{status: string, plans: RechargePlan[]}> {
    return this.http.get<{status: string, plans: RechargePlan[]}>(this.plansUrl, {
      withCredentials: true
    });
  }

  loadMSwipeScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof (window as any).MSwipe !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://sandbox-pg.mswipe.in/sdk/v1/mswipe-pg-sdk.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load MSwipe script'));
      document.body.appendChild(script);
    });
  }


 initiateMSwipePaymentAuto(planId: number, userData: any): Observable<any> {
    return new Observable(observer => {
      // Pehle transaction create karo
      this.initiatePayment(planId).subscribe({
        next: (transactionResponse) => {
          if (transactionResponse.status === 'success') {
            
            // MSwipe token generate karo
            this.mswipeService.generateToken().subscribe({
              next: (tokenResponse) => {
                if (tokenResponse && tokenResponse.status === 'true') {
                  
                  // MSwipe payment initiate karo
                  const paymentData = {
                    amount: userData.amount.toString(),
                    user_id: userData.userId.toString(),
                    email_id: userData.email,
                    invoice_id: transactionResponse.transaction_id,
                    request_id: `REQ${Date.now()}`,
                    redirect_url: `https://stories.funtelstudio.com/payment-success?txn=${transactionResponse.transaction_id}`,
                    paymentreason: `Wallet Recharge - Plan ${planId}`,
                    sessiontoken: tokenResponse.token
                  };

                  this.mswipeService.initiatePayment(paymentData).subscribe({
                    next: (mswipeResponse) => {
                      console.log('MSwipe Auto Response:', mswipeResponse);
                      
                      if (mswipeResponse && mswipeResponse.status === 'True' && mswipeResponse.smslink) {
                        // AUTOMATIC POPUP - Razorpay jaisa
                        this.openMSwipePopup(mswipeResponse.smslink, transactionResponse.transaction_id, observer);
                      } else {
                        observer.error(new Error('Payment link generation failed'));
                      }
                    },
                    error: (error) => {
                      observer.error(error);
                    }
                  });
                  
                } else {
                  observer.error(new Error('Token generation failed'));
                }
              },
              error: (error) => {
                observer.error(error);
              }
            });
            
          } else {
            observer.error(new Error('Transaction creation failed'));
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }
  private openMSwipePopup(paymentLink: string, transactionId: string, observer: any) {
    // Popup dimensions
    const width = 500;
    const height = 700;
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;
    
    // Popup open karo
    const popup = window.open(
      paymentLink, 
      'MSwipePayment', 
      `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
    );
    
    if (!popup) {
      observer.error(new Error('Popup blocked! Please allow popups for this site.'));
      return;
    }
    
    // Popup check karo har 500ms mein
    const popupCheck = setInterval(() => {
      if (popup.closed) {
        clearInterval(popupCheck);
        observer.next({
          status: 'popup_closed',
          message: 'Payment popup closed by user',
          transaction_id: transactionId
        });
        observer.complete();
      }
    }, 500);
    
    // Store popup reference
    (window as any).mswipePopup = popup;
  }

  initiateDirectPayment(amount: number, description: string): Observable<PaymentResponse> {
    return this.http.post<PaymentResponse>(this.paymentUrl, 
      { 
        amount: amount,
        payment_gateway: 'mswipe',
        product_description: description
      },
      { withCredentials: true }
    );
  }

verifyMSwipePayment(verifyData: VerifyPaymentRequest): Observable<VerifyPaymentResponse> {
    return this.http.post<VerifyPaymentResponse>(this.verifyUrl, verifyData, { 
      withCredentials: true 
    }).pipe(
      tap(response => console.log('Verify Payment Response:', response)),
      catchError(error => {
        console.error('Verify Payment Error:', error);
        return throwError(() => error);
      })
    );
  }
  checkPaymentStatus(transactionId: string): Observable<any> {
    return new Observable(observer => {
      this.mswipeService.getMSwipeResponse(transactionId).subscribe({
        next: (response) => {
          if (response.status === 'success' && response.mswipe_response) {
            const mswipeResponse = response.mswipe_response;
            const transId = this.mswipeService.extractTransIdFromLink(mswipeResponse.smslink);
            
            if (transId) {
              this.mswipeService.getTransactionStatus(transId).subscribe({
                next: (statusResponse) => {
                  observer.next(statusResponse);
                  observer.complete();
                },
                error: (statusError) => {
                  observer.error(statusError);
                }
              });
            } else {
              observer.error('No TransID found');
            }
          } else {
            observer.error('No MSwipe response found');
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

 
getStoredMSwipeResponse(transactionId: string): Observable<{status: string, mswipe_response: MSwipePaymentResponse}> {
    return this.mswipeService.getMSwipeResponse(transactionId);
  }

  initiatePayment(planId: number): Observable<PaymentResponse> {
    return this.http.post<PaymentResponse>(this.paymentUrl, 
      { plan_id: planId },
      { withCredentials: true }
    );
  }

}